import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';
import { clientLandingPath } from './utils/clientAccess';
import AdminAnnouncements from './pages/AdminAnnouncements';
import AdminPanel from './pages/AdminPanel';
import AluminiumDeals from './pages/AluminiumDeals';
import AssignedRequests from './pages/AssignedRequests';
import AttachmentPreview from './pages/AttachmentPreview';
import Colleagues from './pages/Colleagues';
import ClientAluminiumOffers from './pages/ClientAluminiumOffers';
import ContentManagement from './pages/ContentManagement';
import EmployeeManager from './pages/EmployeeManager';
import FeedbackManagement from './pages/FeedbackManagement';
import ForgotPassword from './pages/ForgotPassword';
import GoogleReviews from './pages/GoogleReviews';
import Home from './pages/Home';
import HR from './pages/HR';
import InstallationPlanning from './pages/InstallationPlanning';
import Login from './pages/Login';
import Logistics from './pages/Logistics';
import Management from './pages/Management';
import MeetingManager from './pages/MeetingManager';
import MeetingStatistics from './pages/MeetingStatistics';
import Montaz from './pages/Montaz';
import NoRole from './pages/NoRole';
import Notifications from './pages/Notifications';
import OrderAnalytics from './pages/OrderAnalytics';
import Organization from './pages/Organization';
import TasksProjects from './pages/TasksProjects';
import ToolAnalytics from './pages/ToolAnalytics';
import Production from './pages/Production';
import ProductionBlindsPredictions from './pages/ProductionBlindsPredictions';
import ProductionGlassMonitoring from './pages/ProductionGlassMonitoring';
import ProductionOrderMonitoring from './pages/ProductionOrderMonitoring';
import ProductionSchucoMonitoring from './pages/ProductionSchucoMonitoring';
import Profile from './pages/Profile';
import Register from './pages/Register';
import Sales from './pages/Sales';
import SchucoOrderAnalytics from './pages/SchucoOrderAnalytics';
import Settings from './pages/Settings';
import StojkiAutoEmail from './pages/StojkiAutoEmail';
import Surveys from './pages/Surveys';
import Trainings from './pages/Trainings';
import WinkhausOrderAnalytics from './pages/WinkhausOrderAnalytics';

function App() {
  const { loading, isAuthenticated, hasAnyRoleAssigned, hasPermission, hasRole, user } = useAuth();
  const isClient = hasRole('client');
  const startPath = isClient ? clientLandingPath(user?.permissions) : '/home';
  const aluminiumDealsStartPath = hasPermission('aluminium_deals:use')
    ? '/sales/aluminium-deals/discount'
    : (hasPermission('aluminium_deals:montage')
      ? '/sales/aluminium-deals/montage'
      : '/sales/aluminium-deals/translation');

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Client accounts stay on the access-pending screen until a tool is granted.
  if (isAuthenticated && (!hasAnyRoleAssigned() || (isClient && !startPath))) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<NoRole />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to={startPath} replace /> : <Login />
          } 
        />
        <Route 
          path="/register" 
          element={
            isAuthenticated ? <Navigate to={startPath} replace /> : <Register />
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            isAuthenticated ? <Navigate to={startPath} replace /> : <ForgotPassword />
          } 
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Layout><Home /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hr"
          element={
            <ProtectedRoute requiredPermissions={['hr:view', 'hr:manage_employees']}>
              <Layout><HR /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hr/employee-manager"
          element={
            <ProtectedRoute requiredPermission="hr:manage_employees">
              <Layout><EmployeeManager /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRoute>
              <Layout><Sales /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/meeting-manager"
          element={
            <ProtectedRoute requiredPermission="meetings:manage">
              <Layout><MeetingManager /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/aluminium-deals"
          element={
            <ProtectedRoute requiredPermissions={["aluminium_deals:use", "aluminium_deals:montage", "aluminium_deals:translation"]}>
              <Navigate to={aluminiumDealsStartPath} replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute requiredPermission="clients:aluminium_offers">
              <Navigate to="/clients/aluminium-offers" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/aluminium-offers"
          element={
            <ProtectedRoute requiredPermission="clients:aluminium_offers">
              <Layout><ClientAluminiumOffers /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/aluminium-deals/discount"
          element={
            <ProtectedRoute requiredPermission="aluminium_deals:use">
              <Layout><AluminiumDeals section="discount" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/aluminium-deals/montage"
          element={
            <ProtectedRoute requiredPermission="aluminium_deals:montage">
              <Layout><AluminiumDeals section="montage" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/aluminium-deals/translation"
          element={
            <ProtectedRoute requiredPermission="aluminium_deals:translation">
              <Layout><AluminiumDeals section="translation" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/assigned-requests"
          element={
            <ProtectedRoute requiredPermission="meetings:assignable">
              <Layout><AssignedRequests /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/statistics"
          element={
            <ProtectedRoute requiredPermission="meetings:statistics">
              <Layout><MeetingStatistics /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/tasks"
          element={
            <ProtectedRoute requiredPermission="tasks:use">
              <Layout><TasksProjects /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/tasks/:organizationId"
          element={
            <ProtectedRoute requiredPermission="tasks:use">
              <Layout><TasksProjects /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <ProtectedRoute requiredPermissions={['organization:view', 'tasks:use', 'survey:fill', 'survey:view_results', 'survey:manage']}>
              <Layout><Organization /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/surveys"
          element={
            <ProtectedRoute requiredPermissions={['survey:fill', 'survey:view_results', 'survey:manage']}>
              <Layout><Surveys /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/surveys/:surveyId"
          element={
            <ProtectedRoute requiredPermissions={['survey:fill', 'survey:view_results', 'survey:manage']}>
              <Layout><Surveys /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/logistics"
          element={
            <ProtectedRoute requiredPermissions={['module:logistics', 'email_logistics:view', 'email_logistics:manage']}>
              <Layout><Logistics /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/logistics/stojaki-auto-email"
          element={
            <ProtectedRoute requiredPermissions={['email_logistics:view', 'email_logistics:manage']}>
              <Layout><StojkiAutoEmail /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production"
          element={
            <ProtectedRoute>
              <Layout><Production /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production/order-monitoring"
          element={
            <ProtectedRoute requiredPermission="production:monitor_orders">
              <Layout><ProductionOrderMonitoring /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production/schuco-monitoring"
          element={
            <ProtectedRoute requiredPermission="production:monitor_schuco">
              <Layout><ProductionSchucoMonitoring /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production/blinds"
          element={
            <ProtectedRoute requiredPermission="production:monitor_blinds">
              <Layout><ProductionBlindsPredictions /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production/glass-monitoring"
          element={
            <ProtectedRoute requiredPermission="production:monitor_glass">
              <Layout><ProductionGlassMonitoring /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout><Notifications /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout><Profile /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/colleagues"
          element={
            <ProtectedRoute>
              <Layout><Colleagues /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout><Settings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminPanel /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/content-management"
          element={
            <ProtectedRoute requiredPermissions={[
              'content:manage_news',
              'content:manage_announcements',
              'content:manage_announcements_global',
              'content:manage_announcements_department',
            ]}>
              <Layout><ContentManagement /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-announcements"
          element={
            <ProtectedRoute requiredPermission="content:manage_announcements">
              <Layout><AdminAnnouncements /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/feedback-management"
          element={
            <ProtectedRoute requiredPermission="feedback:manage">
              <Layout><FeedbackManagement /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/trainings"
          element={
            <ProtectedRoute requiredPermission="trainings:view">
              <Layout><Trainings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attachment-preview/:consultationId/:attachmentId"
          element={
            <ProtectedRoute>
              <AttachmentPreview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management"
          element={
            <ProtectedRoute requiredPermissions={['management:view', 'reviews:view', 'order_analytics:view', 'tool_analytics:view']}>
              <Layout><Management /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/google-reviews"
          element={
            <ProtectedRoute requiredPermission="reviews:view">
              <Layout><GoogleReviews /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/tool-analytics"
          element={
            <ProtectedRoute requiredPermission="tool_analytics:view">
              <Layout><ToolAnalytics /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/order-analytics"
          element={
            <ProtectedRoute requiredPermission="order_analytics:view">
              <Layout><OrderAnalytics /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/order-analytics/winkhaus"
          element={
            <ProtectedRoute requiredPermission="order_analytics:view">
              <Layout><WinkhausOrderAnalytics /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/order-analytics/schuco"
          element={
            <ProtectedRoute requiredPermission="order_analytics:view">
              <Layout><SchucoOrderAnalytics /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/montaz"
          element={
            <ProtectedRoute requiredPermissions={['installation:view', 'installation:manage', 'installation:settlement']}>
              <Layout><Montaz /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/montaz/planowanie"
          element={
            <ProtectedRoute requiredPermissions={['installation:view', 'installation:manage', 'installation:settlement']}>
              <Layout><InstallationPlanning /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/montaz/rozliczanie"
          element={<Navigate to="/montaz/planowanie?tab=rozliczanie" replace />}
        />
        <Route 
          path="/" 
          element={<Navigate to={isAuthenticated ? startPath : "/login"} replace />}
        />
        <Route 
          path="*" 
          element={<Navigate to="/" replace />} 
        />
      </Routes>
    </Router>
  );
}

export default App;
